import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AmcLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { AmcGeoResponse, AmcGeoTableRow } from '../amc-geo-types';

// The API returns rate fields as raw strings/numbers of varying precision; coerce them to clean
// numbers (defaulting missing/zero values to 0) so the map, cards, and table can rely on them.
const sanitizeRow = (row: AmcGeoTableRow): AmcGeoTableRow => ({
  ...row,
  dpvRate: row.dpvRate ? parseFloat(String(row.dpvRate)) : 0,
  conversionRate: row.conversionRate ? parseFloat(String(row.conversionRate)) : 0,
  ntbRepeatRate: row.ntbRepeatRate ? parseFloat(String(row.ntbRepeatRate)) : 0,
  ntbPurchaseRate: row.ntbPurchaseRate ? parseFloat(String(row.ntbPurchaseRate)) : 0
});

const fetchAmcGeoData = async (
  amcInstanceId: string,
  startDate: string,
  endDate: string,
  state: string | null
): Promise<AmcGeoResponse> => {
  const response = await AmcLambdaFetch('/getGeoReport', {
    params: { amcInstanceId, startDate, endDate, duration: 'monthly', state }
  });
  const json = await awaitJSON<AmcGeoResponse>(response);
  return { ...json, data: json.data.map(sanitizeRow), total: sanitizeRow(json.total) };
};

export const useAmcGeoData = (
  amcInstanceId: string | undefined,
  startDate: string,
  endDate: string,
  state: string | null
): ReturnType<typeof useQuery<AmcGeoResponse>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['amcGeoData', amcInstanceId, startDate, endDate, state],
    queryFn: () => {
      if (!amcInstanceId) throw new Error('Missing AMC instance id');
      return fetchAmcGeoData(amcInstanceId, startDate, endDate, state);
    },
    enabled: Boolean(amcInstanceId),
    // Keep the prior selection's data on screen while a newly-selected date range or state loads.
    placeholderData: keepPreviousData
  });
